import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Link,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  type FunnelPageRow,
  type FunnelPatchFields,
  type FunnelSettingsInfo,
  getFunnelSettings,
  getKampagnenInfo,
  type KampagnenInfo,
  patchFunnelContent,
  putFunnelSettings,
} from './api';
import { Abschnitt, Feld, useFeldpruefung } from '../../../components/marketingForm';
import { formatBerlin, Loading } from './ui';

/**
 * Reiter „Einstellungen" der Strecke (Spec § 3/§ 4):
 *  - Strecken-Einstellungen (lp_funnel_settings): Stichtag (Berliner Datum + Uhrzeit), Upsell-Produkt.
 *  - Felder der Bestellseite: PL-X-Kampagne, Preiszeile, Countdown (scarcity_timer).
 *
 * Zwei Schreibwege, beide nur mit Geaendertem: PUT /settings (voller Ersatz — der Reiter kennt
 * beide Felder, also nichts Fremdes zu verlieren) und PATCH …/order/content (nur geaenderte Felder).
 * Eine Einstellungsaenderung markiert alle Seiten der Strecke als geaendert (Fingerabdruck, § 4) —
 * die Pruefung muss danach neu laufen.
 */

interface Props {
  webinarId: string;
  /** Bestellseite; null = noch nicht angelegt (Felder gesperrt, Einstellungen gehen trotzdem). */
  orderPage: FunnelPageRow | null;
  onSaved: (label: string, warnings: string[]) => void;
  onDirtyChange: (dirty: boolean) => void;
}

interface Form {
  stichtagDatum: string;
  stichtagZeit: string;
  upsellProdukt: string;
  kampagne: string;
  preis: string;
  countdown: boolean;
}

/** Ergebnis des Kampagnen-Abrufs, gebunden an Webinar UND Nummer (sonst stuende eine alte Antwort unter neuer Eingabe). */
interface KampagnenAbruf {
  webinarId: string;
  id: string;
  data: KampagnenInfo | null;
  error: string | null;
}

/**
 * Zeile unter dem Feld „PL-X-Kampagne": WKZ · Produkt · Preis aus PL-X und die Befunde der Strecken-Pruefung
 * (node-app regeln.ts kampagnenBefunde — dieselbe Regel, die den Publish sperrt).
 */
function KampagnenInfoZeile({ abruf }: { abruf: KampagnenAbruf | null | 'laeuft' }) {
  if (!abruf) return null;
  if (abruf === 'laeuft') {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        PL-X wird gefragt …
      </Typography>
    );
  }
  if (abruf.error || !abruf.data) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        Kampagne konnte nicht geprüft werden: {abruf.error || 'keine Antwort'}
      </Alert>
    );
  }
  const { kampagne, erwartetesProdukt, befunde } = abruf.data;
  const fehler = befunde.some((b) => b.schwere === 'fehler');
  return (
    <Box sx={{ mt: 0.75 }}>
      {kampagne.status === 'ok' && (
        <Typography variant="body2" sx={{ mb: befunde.length ? 1 : 0 }}>
          <b>WKZ {kampagne.wkz}</b>
          {kampagne.produktTitel ? ` · ${kampagne.produktTitel}` : ''}
          {kampagne.preis ? ` · ${kampagne.preis}` : ''}
          {!fehler && kampagne.config && erwartetesProdukt && (
            <Box component="span" sx={{ color: '#2e7d32' }}>
              {' '}
              ✓ passt zum Produkt {erwartetesProdukt.code}
            </Box>
          )}
          {!erwartetesProdukt && (
            <Box component="span" sx={{ color: 'text.secondary' }}>
              {' '}
              · Webinar hat kein Produkt hinterlegt
            </Box>
          )}
        </Typography>
      )}
      {befunde.map((b, i) => (
        <Alert
          key={`${b.regel}-${i}`}
          severity={b.schwere === 'fehler' ? 'error' : b.schwere === 'warnung' ? 'warning' : 'info'}
          sx={{ mt: i ? 0.75 : 0 }}
        >
          {b.text}
          {b.schwere === 'fehler' && ' — so lässt sich die Strecke nicht veröffentlichen.'}
          {b.stelle && (
            <Typography variant="caption" sx={{ display: 'block' }}>
              {b.stelle}
            </Typography>
          )}
          {b.link && (
            <>
              {' '}
              <Link href={b.link} target="_blank" rel="noopener">
                Öffnen
              </Link>
            </>
          )}
        </Alert>
      ))}
    </Box>
  );
}

function splitWallclock(v: string | null): { datum: string; zeit: string } {
  const m = v ? /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/.exec(v) : null;
  return m ? { datum: m[1], zeit: m[2] } : { datum: '', zeit: '' };
}

export default function SettingsPanel({ webinarId, orderPage, onSaved, onDirtyChange }: Props) {
  const [info, setInfo] = useState<FunnelSettingsInfo | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  // Regel-Pruefung der Preiszeile (Woche UND Monat, fremder Name, Termin) beim Verlassen — nur Hinweis.
  const preisPruefung = useFeldpruefung('strecke.order.price_text', webinarId);
  const preisPruefen = preisPruefung.pruefen;

  const start: Form | null = useMemo(() => {
    if (!info) return null;
    const s = splitWallclock(info.stichtagAt);
    return {
      stichtagDatum: s.datum,
      stichtagZeit: s.zeit,
      upsellProdukt: info.upsellProdukt || '',
      kampagne: orderPage?.plx_campaign_id || '',
      preis: orderPage?.price_text || '',
      countdown: orderPage ? orderPage.scarcity_timer !== 0 : true,
    };
  }, [info, orderPage]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    (async () => {
      const res = await getFunnelSettings(webinarId);
      if (cancelled) return;
      if (!res.success || !res.data) {
        setLoadError(res.error || 'Einstellungen konnten nicht geladen werden');
        return;
      }
      setInfo(res.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [webinarId, reload]);

  // Gespeicherte Preiszeile einmal beim Laden pruefen (Persona-Runde 3: „nur Wochenpreis“ blieb
  // sonst unbemerkt, bis jemand ins Feld klickte). Gleicher Wert = keine neue Anfrage (Hook).
  const gespeicherterPreis = start?.preis ?? '';
  useEffect(() => {
    if (gespeicherterPreis.trim()) void preisPruefen(gespeicherterPreis);
  }, [gespeicherterPreis, preisPruefen]);

  // Formular erst NACH dem Laden belegen — und neu, wenn sich der gespeicherte Stand aendert.
  useEffect(() => {
    setForm(start);
  }, [start]);

  const settingsChanged =
    !!form &&
    !!start &&
    (form.stichtagDatum !== start.stichtagDatum ||
      form.stichtagZeit !== start.stichtagZeit ||
      form.upsellProdukt.trim() !== start.upsellProdukt);
  const pageChanged: FunnelPatchFields = useMemo(() => {
    const out: FunnelPatchFields = {};
    if (!form || !start || !orderPage) return out;
    if (form.kampagne.trim() !== start.kampagne) out.plx_campaign_id = form.kampagne.trim() || null;
    if (form.preis.trim() !== start.preis.trim()) out.price_text = form.preis.trim() || null;
    if (form.countdown !== start.countdown) out.scarcity_timer = form.countdown;
    return out;
  }, [form, start, orderPage]);
  const dirty = settingsChanged || Object.keys(pageChanged).length > 0;

  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);

  const kampagneOk = !form?.kampagne.trim() || /^\d{1,20}$/.test(form.kampagne.trim());

  // Kampagnen-Info am Feld: bei JEDER Eingabeaenderung sofort leeren, dann nach 500 ms fragen. Eine Antwort
  // zaehlt nur, wenn Webinar und Nummer noch der Eingabe entsprechen (sonst stuende TAP0005 unter 105108).
  const kampagneEingabe = form?.kampagne.trim() ?? '';
  const hatBestellseite = !!orderPage;
  const [kampagnenAbruf, setKampagnenAbruf] = useState<KampagnenAbruf | null>(null);
  useEffect(() => {
    setKampagnenAbruf(null);
    if (!hatBestellseite || !/^\d{1,20}$/.test(kampagneEingabe)) return;
    let cancelled = false;
    const t = window.setTimeout(async () => {
      const res = await getKampagnenInfo(webinarId, kampagneEingabe);
      if (cancelled) return;
      setKampagnenAbruf({
        webinarId,
        id: kampagneEingabe,
        data: res.success ? (res.data ?? null) : null,
        error: res.success ? null : res.error || 'Abruf fehlgeschlagen',
      });
    }, 500);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [webinarId, kampagneEingabe, hatBestellseite]);
  const kampagnenAnzeige: KampagnenAbruf | null | 'laeuft' =
    !hatBestellseite || !/^\d{1,20}$/.test(kampagneEingabe)
      ? null
      : kampagnenAbruf &&
          kampagnenAbruf.webinarId === webinarId &&
          kampagnenAbruf.id === kampagneEingabe
        ? kampagnenAbruf
        : 'laeuft';
  const stichtagHalb = !!form && !!form.stichtagDatum !== !!form.stichtagZeit;

  const save = useCallback(async () => {
    if (!form || !dirty) return;
    if (!kampagneOk) {
      setError('PL-X-Kampagne: nur Ziffern.');
      return;
    }
    if (stichtagHalb) {
      setError('Stichtag: Datum UND Uhrzeit angeben (oder beide leeren).');
      return;
    }
    setBusy(true);
    setError(null);
    const teile: string[] = [];
    const warnings: string[] = [];
    if (settingsChanged) {
      const stichtagAt = form.stichtagDatum
        ? `${form.stichtagDatum} ${form.stichtagZeit}:00`
        : null;
      const res = await putFunnelSettings(webinarId, {
        stichtagAt,
        upsellProdukt: form.upsellProdukt.trim() || null,
      });
      if (!res.success) {
        setBusy(false);
        setError(`Einstellungen nicht gespeichert: ${res.error || 'unbekannter Fehler'}`);
        return;
      }
      teile.push('Strecken-Einstellungen');
    }
    if (Object.keys(pageChanged).length) {
      const res = await patchFunnelContent(webinarId, 'order', pageChanged, 'code');
      if (!res.success || !res.data) {
        setBusy(false);
        setReload((n) => n + 1);
        setError(
          `Bestellseiten-Felder nicht gespeichert: ${res.error || 'unbekannter Fehler'}` +
            (teile.length ? ' (die Strecken-Einstellungen sind gespeichert)' : ''),
        );
        return;
      }
      warnings.push(...(res.data.warnings || []));
      teile.push(`Bestellseite (Version ${res.data.versionId})`);
    }
    setBusy(false);
    setReload((n) => n + 1);
    onSaved(`Gespeichert: ${teile.join(' + ')} — bitte die Strecke neu prüfen`, warnings);
  }, [form, dirty, kampagneOk, stichtagHalb, settingsChanged, pageChanged, webinarId, onSaved]);

  if (loadError) return <Alert severity="error">{loadError}</Alert>;
  if (!form || !info) return <Loading label="Einstellungen laden …" />;

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {error && <Alert severity="error">{error}</Alert>}

      <Abschnitt
        titel="Bestellseite"
        erklaerung="Ohne PL-X-Kampagne lässt sich die Strecke nicht veröffentlichen. Alles andere ist optional."
      >
        {!orderPage && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Noch keine Bestellseite — erst im Schritt „Seiten hochladen“ anlegen, dann Kampagne und
            Preis eintragen.
          </Alert>
        )}
        <Feld
          titel="PL-X-Kampagne"
          art="pflicht"
          wofuer={
            <>
              Nummer der Bestell-Kampagne bei PL-X — das Bestellformular bucht darauf, und ihre WKZ
              bestimmt, was PL-X dem Kunden liefert und berechnet. Sie steht in der PL-X-Kampagne
              (PL-X/TYPO3: Liste → Kampagnen, Suche nach der WKZ oder dem Produktkürzel, z. B.
              „TAP“; Art „Standard“). Unter dem Feld erscheinen WKZ, Produkt und Preis laut PL-X.
              Wechsel der WKZ = Kampagne mit der neuen WKZ eintragen, dann prüfen und
              veröffentlichen. <b>Ohne PL-X-Zugang beim Admin (Till) erfragen.</b> Nur Ziffern.
            </>
          }
          beispiele={
            orderPage
              ? {
                  feld: 'strecke.order.plx_campaign_id',
                  webinarId,
                  onUebernehmen: (w) => setForm((prev) => (prev ? { ...prev, kampagne: w } : prev)),
                  hinweis: 'Nur übernehmen, wenn es wirklich dieselbe Kampagne ist.',
                }
              : undefined
          }
        >
          <TextField
            size="small"
            value={form.kampagne}
            disabled={!orderPage}
            onChange={(e) =>
              setForm((prev) => (prev ? { ...prev, kampagne: e.target.value } : prev))
            }
            error={!kampagneOk || (!!orderPage && !form.kampagne.trim())}
            helperText={
              !kampagneOk
                ? 'Nur Ziffern'
                : orderPage && !form.kampagne.trim()
                  ? 'Fehlt noch — ohne Kampagne kein Veröffentlichen'
                  : undefined
            }
            inputProps={{ 'aria-label': 'PL-X-Kampagne' }}
            sx={{ width: 300 }}
          />
          <KampagnenInfoZeile abruf={kampagnenAnzeige} />
        </Feld>
        <Feld
          titel="Preiszeile"
          art="optional"
          wofuer="Klebende Leiste am unteren Rand der Bestellseite. Preis pro Woche UND pro Monat nennen; muss zur Seite und zum Formular passen. Leer = keine Leiste."
          beispiele={
            orderPage
              ? {
                  feld: 'strecke.order.price_text',
                  webinarId,
                  onUebernehmen: (w) => setForm((prev) => (prev ? { ...prev, preis: w } : prev)),
                  hinweis: 'Preise und Fristen bitte prüfen — sie ändern sich je Angebot.',
                }
              : undefined
          }
          befunde={preisPruefung.befunde}
          pruefungLaeuft={preisPruefung.laeuft}
        >
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.preis}
            disabled={!orderPage}
            onChange={(e) => setForm((prev) => (prev ? { ...prev, preis: e.target.value } : prev))}
            onBlur={() => void preisPruefung.pruefen(form.preis)}
            inputProps={{ 'aria-label': 'Preiszeile' }}
          />
        </Feld>
        <Feld
          titel="Countdown"
          art="optional"
          wofuer="Zeigt den Countdown an der Stelle, an der die Seite den Countdown-Platz hat. Aus = kein Countdown."
        >
          <FormControlLabel
            control={
              <Switch
                checked={form.countdown}
                disabled={!orderPage}
                onChange={(e) =>
                  setForm((prev) => (prev ? { ...prev, countdown: e.target.checked } : prev))
                }
              />
            }
            label={form.countdown ? 'Countdown an' : 'Countdown aus'}
          />
        </Feld>
      </Abschnitt>

      <Abschnitt
        titel="Gilt für alle drei Seiten"
        art="optional"
        erklaerung="Diese Einstellungen wirken auf Bestellseite, Upsell und Danke zugleich."
      >
        <Feld
          titel="Stichtag"
          art="optional"
          wofuer={
            <>
              Datum, ab dem die Seiten andere Texte zeigen — z. B. Bonusende. Leer = kein Wechsel.
              Technisch: speist {'{STICHTAG_DATUM}'}, {'{STICHTAG_ZEIT}'}, {'{STICHTAG_RELATIV}'}{' '}
              und die Abschnitte „nur vor/nach dem Stichtag“.
            </>
          }
        >
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-start">
            <TextField
              label="Datum"
              type="date"
              size="small"
              value={form.stichtagDatum}
              onChange={(e) =>
                setForm((prev) => (prev ? { ...prev, stichtagDatum: e.target.value } : prev))
              }
              InputLabelProps={{ shrink: true }}
              error={stichtagHalb && !form.stichtagDatum}
            />
            <TextField
              label="Uhrzeit (Berlin)"
              type="time"
              size="small"
              value={form.stichtagZeit}
              onChange={(e) =>
                setForm((prev) => (prev ? { ...prev, stichtagZeit: e.target.value } : prev))
              }
              InputLabelProps={{ shrink: true }}
              error={stichtagHalb && !form.stichtagZeit}
              helperText="deutsche Zeit"
            />
            <Button
              size="small"
              sx={{ mt: 0.5 }}
              onClick={() =>
                setForm((prev) => (prev ? { ...prev, stichtagDatum: '', stichtagZeit: '' } : prev))
              }
              disabled={!form.stichtagDatum && !form.stichtagZeit}
            >
              Kein Stichtag
            </Button>
          </Box>
        </Feld>
        <Feld
          titel="Upsell-Produkt"
          art="optional"
          wofuer="Nur nötig, wenn der Upsell ein anderes Produkt verkauft als das Webinar. Dann den Namen dieses Produkts eintragen — sonst meldet die KI-Prüfung es als „fremdes Produkt“. Leer = Upsell verkauft dasselbe Produkt."
          beispiele={{
            feld: 'strecke.upsell_produkt',
            webinarId,
            onUebernehmen: (w) =>
              setForm((prev) => (prev ? { ...prev, upsellProdukt: w } : prev)),
          }}
        >
          <TextField
            size="small"
            fullWidth
            value={form.upsellProdukt}
            onChange={(e) =>
              setForm((prev) => (prev ? { ...prev, upsellProdukt: e.target.value } : prev))
            }
            inputProps={{ 'aria-label': 'Upsell-Produkt' }}
          />
        </Feld>
        {info.vorhanden && (
          <Typography variant="caption" color="text.secondary" display="block">
            Zuletzt geändert {formatBerlin(info.updatedAt)}
            {info.updatedBy ? ` von ${info.updatedBy}` : ''}
          </Typography>
        )}
      </Abschnitt>

      <Box display="flex" alignItems="center" gap={1}>
        <Button
          variant="contained"
          onClick={save}
          disabled={busy || !dirty || !kampagneOk || stichtagHalb}
        >
          {busy ? 'Speichert …' : 'Einstellungen speichern'}
        </Button>
        <Button onClick={() => setForm(start)} disabled={busy || !dirty}>
          Verwerfen
        </Button>
        {dirty && (
          <Typography variant="caption" color="text.secondary">
            Ungespeicherte Änderungen
          </Typography>
        )}
      </Box>
    </Box>
  );
}
